import { NaturalPerson, LegalPerson } from 'api';
import Date from 'ui/atoms/date';
import PersonName from 'ui/atoms/person-name';
import { compact } from 'lodash';
import { hideCentralCharacters } from 'ui/helper/hideCharacters';
import DocumentModal from 'src/libraries/document-modal';
import React from 'react';
import Translate from 'ui/atoms/translate';
import PlaceholderFallback from 'ui/atoms/placeholder-fallback';
import Currency from 'ui/atoms/currency';

interface UserDataProps {
  rootPerson?: LegalPerson | NaturalPerson | null;
  naturalPerson?: NaturalPerson | null;
  legalPerson?: LegalPerson | null;
  setEditTaxInformationModal: (val: boolean) => void;
  setEditBankAccountModal: (val: boolean) => void;
}

const useUserData = (props: UserDataProps) => {
  const { rootPerson, naturalPerson, legalPerson, setEditTaxInformationModal, setEditBankAccountModal } = props;

  const userData = {
    personalData: {
      key: 'personalData',
      fields: {
        name: (
          <PersonName
            person={{
              forename: naturalPerson?.forename,
              surname: naturalPerson?.surname,
              companyName: legalPerson?.companyName,
            }}
          />
        ),
        birthDate: naturalPerson?.birthDate && <Date>{naturalPerson?.birthDate}</Date>,
        birthPlace: naturalPerson?.birthPlace,
        usTax: naturalPerson?.isSubjectToUsTax ? (
          <Translate name={`commonOptions.true`} />
        ) : (
          <Translate name={`commonOptions.false`} />
        ),
      },
      editButtons: [
        {
          display: false,
          text: 'common.edit',
          onClick: () => {},
        },
      ],
    },
    registrationAddress: {
      key: 'registrationAddress',
      fields: {
        street: naturalPerson?.street,
        city: compact([naturalPerson?.zip, naturalPerson?.city]).join(' '),
        country: naturalPerson?.country && <Translate name={`countries.${naturalPerson?.country}`} />,
      },
      editButtons: [
        {
          display: false,
          text: 'common.edit',
          onClick: () => {},
        },
      ],
    },
    taxData: {
      key: 'taxData',
      fields: {
        taxIdentificationNumber:
          rootPerson?.taxInformation?.taxIdentificationNumber &&
          hideCentralCharacters(rootPerson.taxInformation.taxIdentificationNumber, 2, -2),
        taxResidency:
          rootPerson?.taxInformation?.isSubjectToGermanTax === undefined ? undefined : rootPerson?.taxInformation
              ?.isSubjectToGermanTax ? (
            <Translate name="dashboardInvestorSettings.taxData.subjectToTax" />
          ) : (
            <Translate name="dashboardInvestorSettings.taxData.notSubjectToTax" />
          ),
        exemptionOrderSum: (
          <PlaceholderFallback>
            {rootPerson?.taxInformation?.exemptionOrderSum && (
              <Currency>{rootPerson.taxInformation.exemptionOrderSum}</Currency>
            )}
          </PlaceholderFallback>
        ),
        nonAssessmentCertificate: rootPerson?.taxInformation?.nonAssessmentCertificate && (
          <Translate
            name="common.viewDocument"
            args={[
              (text: string, key) => {
                return (
                  <DocumentModal key={key} {...rootPerson?.taxInformation?.nonAssessmentCertificateDoc}>
                    {text}
                  </DocumentModal>
                );
              },
            ]}
          />
        ),
      },
      editButtons: [
        {
          display: true,
          text: 'common.edit',
          onClick: () => setEditTaxInformationModal(true),
        },
      ],
    },
    bankData: {
      key: 'bankData',
      fields: {
        bankAccountHolder: rootPerson?.bankAccount?.accountHolder,
        ibanBank: rootPerson?.bankAccount?.iban && hideCentralCharacters(rootPerson.bankAccount.iban, 4, -2),
      },
      editButtons: [
        {
          display: true,
          text: 'common.edit',
          onClick: () => setEditBankAccountModal(true),
        },
      ],
    },
    depositData: {
      key: 'depositData',
      fields: {
        securitiesAccountHolder: rootPerson?.securitiesDepositAccount?.accountHolder,
        securitiesAccountNumber:
          rootPerson?.securitiesDepositAccount?.accountNumber &&
          hideCentralCharacters(rootPerson?.securitiesDepositAccount?.accountNumber, 2, -2),
        bicSecurities: rootPerson?.securitiesDepositAccount?.bic,
      },
      editButtons: [
        {
          display: false,
          text: 'common.edit',
          onClick: () => {},
        },
      ],
    },
    companyData: {
      key: 'companyData',
      fields: {
        companyName: (
          <PersonName
            person={{
              companyName: legalPerson?.companyName,
            }}
          />
        ),
        legalEntityType: legalPerson?.legalEntityType,
        companyStreet: legalPerson?.street,
        companyCity: legalPerson?.city,
        companyCountry: legalPerson?.country && <Translate name={`countries.${legalPerson?.country}`} />,
        usTax: legalPerson?.isSubjectToUsTax ? (
          <Translate name={`commonOptions.true`} />
        ) : (
          <Translate name={`commonOptions.false`} />
        ),
        commercialRegister: compact([legalPerson?.commercialRegister, legalPerson?.commercialRegisterNumber]).join(
          ': ',
        ),
        commercialRegisterStatement: legalPerson?.commercialRegisterStatement && (
          <Translate
            name="common.viewDocument"
            args={[
              (text: string, key) => {
                if (!legalPerson?.commercialRegisterStatement) return;
                return (
                  <DocumentModal key={key} {...legalPerson.commercialRegisterStatement}>
                    {text}
                  </DocumentModal>
                );
              },
            ]}
          />
        ),
        listOfShareholders: legalPerson?.listOfShareholders && (
          <Translate
            name="common.viewDocument"
            args={[
              (text: string, key) => {
                if (!legalPerson?.listOfShareholders) return;
                return (
                  <DocumentModal key={key} {...legalPerson?.listOfShareholders}>
                    {text}
                  </DocumentModal>
                );
              },
            ]}
          />
        ),
        transparencyRegister: legalPerson?.transparencyRegister && (
          <Translate
            name="common.viewDocument"
            args={[
              (text: string, key) => {
                if (!legalPerson?.transparencyRegister) return;
                return (
                  <DocumentModal key={key} {...legalPerson?.transparencyRegister}>
                    {text}
                  </DocumentModal>
                );
              },
            ]}
          />
        ),
      },
      editButtons: [
        {
          display: false,
          text: 'common.edit',
          onClick: () => {},
        },
      ],
    },
  };

  const userSections = [
    userData.personalData,
    userData.registrationAddress,
    userData.taxData,
    userData.bankData,
    legalPerson ? userData.companyData : null,
  ];

  return userSections;
};

export default useUserData;
